import { useEffect, useState } from "react";
import { OfficerLayout } from "@/components/OfficerLayout";
import { StatutoryRegister } from "@/components/StatutoryRegister";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/i18n";
import { Calculator, Zap, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";

const RATE_TYPES = [
  { k: "house_tax_pct", lbl: "House Tax (% of ARV)" },
  { k: "water_tax_flat", lbl: "Water Tax (flat ₹)" },
  { k: "street_light_tax_flat", lbl: "Street Light Tax (flat ₹)" },
  { k: "conservancy_tax_flat", lbl: "Conservancy Tax (flat ₹)" },
  { k: "drainage_tax_flat", lbl: "Drainage Tax (flat ₹)" },
];

export default function TaxEngine() {
  const { lang } = useLang();
  const { user } = useAuth();
  const [tab, setTab] = useState("rules");
  return (
    <OfficerLayout>
      <div>
        <div className="mb-5">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722]">Tax Engine</div>
          <h1 className="mt-2 font-heading text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Calculator className="w-7 h-7 text-[#0A3161]" />
            {lang === "hi" ? "कर नियम एवं वसूली" : "Tax Rules, Bulk Generate & Arrears"}
          </h1>
        </div>
        <div className="flex border-b border-slate-200 mb-4 overflow-x-auto">
          {[
            { k: "rules", label: lang === "hi" ? "कर नियम" : "Tax Rules" },
            { k: "bulk", label: lang === "hi" ? "थोक मांग" : "Bulk Generate" },
            { k: "arrears", label: lang === "hi" ? "बकाया" : "Arrears" },
            { k: "VI", label: lang === "hi" ? "प्रपत्र VI · मांग एवं वसूली रजिस्टर" : "Form VI · Demand & Collection Register" },
          ].map((x) => (
            <button key={x.k} onClick={() => setTab(x.k)} data-testid={`te-tab-${x.k}`}
              className={`px-5 py-3 text-sm font-semibold border-b-2 ${tab === x.k ? "border-[#E87722] text-[#0A3161]" : "border-transparent text-slate-500"}`}>{x.label}</button>
          ))}
        </div>
        {tab === "rules" && <Rules user={user} />}
        {tab === "bulk" && <BulkGen user={user} />}
        {tab === "arrears" && <Arrears user={user} />}
        {tab === "VI" && <StatutoryRegister code="VI" />}
      </div>
    </OfficerLayout>
  );
}

function Rules({ user }) {
  const [rules, setRules] = useState([]);
  const [rateType, setRateType] = useState("house_tax_pct");
  const [ptype, setPtype] = useState("");
  const [value, setValue] = useState("");

  const load = () => {
    if (!user?.panchayat_id) return;
    api.get("/tax/rules", { params: { panchayat_id: user.panchayat_id } }).then((r) => setRules(r.data));
  };
  useEffect(() => { load(); }, [user]);

  const save = async () => {
    if (!user?.panchayat_id) { toast.error("No panchayat scope"); return; }
    try {
      await api.post("/tax/rules", {
        panchayat_id: user.panchayat_id,
        rate_type: rateType,
        property_type: ptype || null,
        value: parseFloat(value),
      });
      toast.success("Rule saved"); setValue(""); load();
    } catch (e) { toast.error("Failed"); }
  };

  const remove = async (id) => {
    await api.delete(`/tax/rules/${id}`);
    load();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 rounded-md border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="rules-table">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            <tr><th className="text-left px-4 py-3">Rate Type</th><th className="text-left px-4 py-3">Property Type</th>
                <th className="text-right px-4 py-3">Value</th><th className="text-right px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-4 py-3 text-xs">{RATE_TYPES.find((x) => x.k === r.rate_type)?.lbl || r.rate_type}</td>
                <td className="px-4 py-3 capitalize text-xs">{r.property_type || "(All)"}</td>
                <td className="px-4 py-3 text-right font-mono">{r.value}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => remove(r.id)} className="text-red-600 hover:bg-red-50 p-1 rounded" data-testid={`rule-del-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
              </tr>
            ))}
            {rules.length === 0 && <tr><td colSpan={4} className="py-10 text-center text-slate-400">No rules — defaults apply</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="rounded-md border border-slate-200 bg-white p-5">
        <h3 className="font-heading font-bold">Add / Update Rule</h3>
        <div className="mt-4 space-y-3 text-sm">
          <div><label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Rate Type</label>
            <select value={rateType} onChange={(e) => setRateType(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm" data-testid="rule-type">
              {RATE_TYPES.map((x) => <option key={x.k} value={x.k}>{x.lbl}</option>)}
            </select>
          </div>
          <div><label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Property Type</label>
            <select value={ptype} onChange={(e) => setPtype(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm" data-testid="rule-ptype">
              <option value="">All</option>
              <option value="residential">Residential</option>
              <option value="commercial">Commercial</option>
              <option value="industrial">Industrial</option>
              <option value="mixed">Mixed</option>
            </select>
          </div>
          <div><label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Value</label>
            <input type="number" step="0.01" value={value} onChange={(e) => setValue(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm font-mono" data-testid="rule-value" />
          </div>
          <button onClick={save} data-testid="rule-save" className="w-full px-3 py-2 text-xs font-semibold rounded-md text-white" style={{ background: "#0A3161" }}>Save</button>
        </div>
      </div>
    </div>
  );
}

function BulkGen({ user }) {
  const [fy, setFy] = useState("2026-27");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (!user?.panchayat_id) { toast.error("No panchayat scope"); return; }
    setBusy(true);
    try {
      const r = await api.post("/tax/demands/bulk-generate", { panchayat_id: user.panchayat_id, financial_year: fy });
      setResult(r.data); toast.success(`${r.data.created} demands generated`);
    } catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-slate-200 bg-white p-7 max-w-2xl" data-testid="bulk-gen">
      <div className="flex items-center gap-2 mb-3 text-amber-700"><Zap className="w-5 h-5" /><h3 className="font-heading text-lg font-bold">Bulk Demand Generation</h3></div>
      <p className="text-sm text-slate-600">Applies your panchayat&apos;s tax rules to <strong>every assessed property</strong> and creates demands for the chosen financial year. Skips properties that already have a demand for this FY.</p>
      <div className="mt-5 flex items-end gap-3">
        <div className="flex-1 max-w-xs">
          <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Financial Year</label>
          <input value={fy} onChange={(e) => setFy(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm" data-testid="bulk-fy" />
        </div>
        <button onClick={run} disabled={busy} data-testid="bulk-run" className="px-4 py-2 text-sm font-semibold rounded-md text-white" style={{ background: "#E87722" }}>{busy ? "Running…" : "Generate Demands"}</button>
      </div>
      {result && (
        <div className="mt-5 grid grid-cols-3 gap-3 text-center" data-testid="bulk-result">
          <Stat label="Created" value={result.created} color="#107C10" />
          <Stat label="Skipped" value={result.skipped} color="#E87722" />
          <Stat label="Total Properties" value={result.total} color="#0A3161" />
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value, color }) => (
  <div className="rounded-md border border-slate-200 p-3"><div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</div><div className="mt-1 font-heading text-2xl font-bold" style={{ color }}>{value}</div></div>
);

function Arrears({ user }) {
  const [data, setData] = useState(null);
  const [minOut, setMinOut] = useState(0);
  const load = () => {
    const params = { min_outstanding: minOut };
    if (user?.panchayat_id) params.panchayat_id = user.panchayat_id;
    api.get("/tax/arrears", { params }).then((r) => setData(r.data));
  };
  useEffect(() => { load(); }, [user, minOut]);
  if (!data) return <div className="text-slate-500 text-sm">Loading…</div>;
  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <AlertTriangle className="w-5 h-5 text-red-600" />
        <div className="text-sm">
          Total arrears: <span className="font-heading font-bold text-red-700">{formatINR(data.total_arrears)}</span>
          <span className="text-slate-500"> across {data.count} defaulters</span>
        </div>
        <div className="ml-auto flex items-center gap-2 text-xs">
          <span className="text-slate-500">Min outstanding ₹</span>
          <input type="number" value={minOut} onChange={(e) => setMinOut(Number(e.target.value))} className="w-24 px-2 py-1 rounded-md border border-slate-300 text-sm font-mono" data-testid="arrears-min" />
        </div>
      </div>
      <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="arrears-table">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            <tr><th className="text-left px-4 py-3">Property No.</th><th className="text-left px-4 py-3">Owner</th>
                <th className="text-left px-4 py-3">Mobile</th><th className="text-left px-4 py-3">Ward</th>
                <th className="text-left px-4 py-3">FY</th><th className="text-right px-4 py-3">Outstanding</th></tr>
          </thead>
          <tbody>
            {data.rows.map((a) => (
              <tr key={a.id} className="border-t border-slate-100">
                <td className="px-4 py-3 font-mono text-xs">{a.property_no}</td>
                <td className="px-4 py-3">{a.owner_name}</td>
                <td className="px-4 py-3 font-mono text-xs">{a.mobile}</td>
                <td className="px-4 py-3">{a.ward}</td>
                <td className="px-4 py-3 font-mono text-xs">{a.financial_year}</td>
                <td className="px-4 py-3 text-right font-mono font-bold text-red-700">{formatINR(a.outstanding)}</td>
              </tr>
            ))}
            {data.rows.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-400">No arrears</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
